import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Pagination,
} from '@nexora/design-system';
import { LEDGER_SIDE_LABEL, BANKING_STATUS_LABEL, bankingStatusVariant } from './useTreasuryLedger';
import type { FinancialLedger } from './useTreasuryLedger';

/* ── Contratos reais (backend/.review-loop/reports/served-routes.json) ──
 * GET /finance/ledger?side&statusIn&q&page&pageSize → FinancialLedgerDto
 *   { items[LedgerItemDto], total, summary{ totalPayables, totalReceivables, scheduledCount } }
 * query: LedgerQueryDto (treasury.dto.ts)
 */

const NUMERIC_COLUMNS = new Set(['Orig. Value', 'Balance']);
const MONEY_CELL = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontVariantNumeric: 'tabular-nums' as const,
  textAlign: 'right' as const,
};

function titleStatusBadge(status: string) {
  const map: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    OPEN: { variant: 'info', label: 'Em aberto' },
    PARTIALLY_SETTLED: { variant: 'warning', label: 'Parcial' },
    SETTLED: { variant: 'success', label: 'Liquidado' },
    OVERDUE: { variant: 'error', label: 'Vencido' },
    RENEGOTIATED: { variant: 'warning', label: 'Renegociado' },
    CANCELLED: { variant: 'neutral', label: 'Cancelado' },
  };
  return map[status] ?? { variant: 'neutral' as const, label: status };
}

export function TreasuryLedgerPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [side, setSide] = useState<'ALL' | 'PAYABLE' | 'RECEIVABLE'>('ALL');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const ledgerQ = useQuery<FinancialLedger>({
    queryKey: ['finance', 'treasury', 'ledger', { page, side, q: debouncedQ }],
    queryFn: () =>
      apiClient.get<FinancialLedger>('/finance/ledger', {
        params: {
          page: String(page),
          pageSize: '20',
          ...(side !== 'ALL' ? { side } : {}),
          ...(debouncedQ ? { q: debouncedQ } : {}),
        },
      }),
  });

  const data = ledgerQ.data;

  const handleSearch = () => {
    setPage(1);
    setDebouncedQ(q.trim());
  };

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / 20));

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Gestão de Títulos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Títulos a pagar e a receber com integração bancária.
          </p>
        </div>
        {data && (
          <div className="flex flex-wrap gap-sm">
            <div className="bg-surface-container-lowest rounded-lg border border-outline-variant px-md py-sm text-center">
              <p className="font-label-sm text-label-sm text-on-surface-variant uppercase m-0">A pagar</p>
              <p className="font-headline-sm text-headline-sm text-on-surface m-0 tabular-nums">{formatCurrency(data.summary.totalPayables)}</p>
            </div>
            <div className="bg-surface-container-lowest rounded-lg border border-outline-variant px-md py-sm text-center">
              <p className="font-label-sm text-label-sm text-on-surface-variant uppercase m-0">A receber</p>
              <p className="font-headline-sm text-headline-sm text-on-surface m-0 tabular-nums">{formatCurrency(data.summary.totalReceivables)}</p>
            </div>
            <div className="bg-surface-container-lowest rounded-lg border border-outline-variant px-md py-sm text-center">
              <p className="font-label-sm text-label-sm text-on-surface-variant uppercase m-0">Agendados p/ remessa</p>
              <p className="font-headline-sm text-headline-sm text-on-surface m-0 tabular-nums">{data.summary.scheduledCount}</p>
            </div>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-surface-container-lowest rounded-xl border border-outline-variant shadow-sm p-md mb-lg flex flex-wrap items-end gap-md">
        <div className="flex flex-col">
          <label htmlFor="ledger-side" className="font-label-sm text-label-sm text-on-surface-variant uppercase mb-xs">Lado</label>
          <select
            id="ledger-side"
            value={side}
            onChange={(e) => { setSide(e.target.value as typeof side); setPage(1); }}
            className="bg-surface-container-low border border-outline-variant font-body-sm text-body-sm rounded py-2 px-sm focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer min-w-[180px]"
          >
            <option value="ALL">Todos</option>
            <option value="PAYABLE">A pagar</option>
            <option value="RECEIVABLE">A receber</option>
          </select>
        </div>
        <div className="flex flex-col flex-1 min-w-[220px]">
          <label htmlFor="ledger-q" className="font-label-sm text-label-sm text-on-surface-variant uppercase mb-xs">Buscar</label>
          <Input
            id="ledger-q"
            placeholder="Documento ou parte..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={handleSearch}>Buscar</Button>
      </div>

      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Razão financeiro</h3>
          <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">{data?.total ?? 0} títulos</span>
        </CardHeader>

        {ledgerQ.isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando razão...</span>
          </div>
        )}

        {ledgerQ.error && !ledgerQ.isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar razão</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>{(ledgerQ.error as Error).message}</p>
          </div>
        )}

        {!ledgerQ.isLoading && !ledgerQ.error && data && data.items.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            Nenhum título encontrado com os filtros atuais.
          </div>
        )}

        {!ledgerQ.isLoading && !ledgerQ.error && data && data.items.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
              <thead>
                <tr>
                  {['ID', 'Parte', 'Orig. Value', 'Balance', 'Orig. Due', 'Curr. Due', 'Método', 'Banking Status', 'Status'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: NUMERIC_COLUMNS.has(h) ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} style={{ transition: 'background 0.15s ease' }} className="ledger-row">
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <span className="font-label-md text-label-md text-primary">
                        {item.side === 'PAYABLE' ? 'AP' : 'AR'} · {item.documentNumber || item.id.slice(0, 8)}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 500 }}>
                      {item.partyName}
                      <span style={{ display: 'block', fontSize: '11px', color: '#74777f' }}>{LEDGER_SIDE_LABEL[item.side]}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', ...MONEY_CELL }}>{formatCurrency(item.originalValue)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', ...MONEY_CELL }}>
                      <span className={item.balance > 0 ? '' : 'text-on-surface-variant'}>{formatCurrency(item.balance)}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>{formatDate(item.originalDueDate)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      {item.currentDueDate !== item.originalDueDate
                        ? <Badge variant="warning" size="sm">{formatDate(item.currentDueDate)}</Badge>
                        : formatDate(item.currentDueDate)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      {item.paymentMethod ?? <span className="text-on-surface-variant">—</span>}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <Badge variant={bankingStatusVariant(item.bankingStatus)} size="sm" dot>
                        {BANKING_STATUS_LABEL[item.bankingStatus] ?? item.bankingStatus}
                      </Badge>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <Badge variant={titleStatusBadge(item.status).variant} size="sm" dot>{titleStatusBadge(item.status).label}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!ledgerQ.isLoading && !ledgerQ.error && data && data.total > 20 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      <style>{`
        .ledger-row:hover td { background: #eceef0; }
      `}</style>
    </div>
  );
}